import {useMemo, useState} from "react";
import {css} from "@panda/css";
import type {Options, Point, TooltipFormatterContextObject} from "highcharts";

import {CenterBox} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Chart, getDefaultXAxisOptions, getDefaultYAxisOptions} from "@pg-mono/charts";
import {formatPL} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {PRICE_STAT_LABEL, PriceStat} from "../../constants/price_stat";
import {TREND_RCN_COLOR, TREND_RP_COLOR} from "../../constants/trend_series_colors";
import {TrendSeriesPoint} from "../../types/TrendSeriesPoint";
import {formatTransactionsCountGenitive} from "../../utils/format_transactions_count";
import {formatPriceAxisLabel, getPriceAxisAllowDecimals} from "../../utils/price_axis";
import {CHART_HEIGHT, PILL_TOP_OFFSET, RCN_SERIES_NAME, RP_SERIES_NAME, TRANSACTIONS_ROW_HEIGHT} from "./constants/chart_constants";
import {
    ChartWithTrendExtras,
    drawTransactionCounts,
    getPreliminaryBandRect,
    getPreliminaryFrom,
    getTooltipPillHtml,
    getTooltipRowHtml,
    PreliminaryBandRect,
    RcnPointCustom,
    setActiveTransactionBar,
    toHighchartsData,
    toRcnHighchartsData
} from "./utils/chart_utils";
import {capitalize} from "./prices_trend_format";
import {ActiveTrendPoint, PricesTrendMobileDetailsPanel} from "./PricesTrendMobileDetailsPanel";
import {PricesTrendPreliminaryPill} from "./PricesTrendPreliminaryPill";

interface Props {
    rpSeries: TrendSeriesPoint[] | null;
    rcnSeries: TrendSeriesPoint[];
    // Points at/after this date render dashed - a visual cue they're still filling in, not settled.
    dashedFromDate?: string;
    priceStat: PriceStat;
    // True while RCN refetches on a filter change - a separate overlay, since rpSeries already has data then.
    isLoading?: boolean;
}

export const PricesTrendChart = (props: Props) => {
    const {rpSeries, rcnSeries, dashedFromDate, priceStat, isLoading} = props;
    const [preliminaryBandRect, setPreliminaryBandRect] = useState<PreliminaryBandRect | null>(null);
    // The point tapped on mobile - drives PricesTrendMobileDetailsPanel as React state, and (via
    // setActiveTransactionBar) the bar outline drawn straight onto the chart's own SVG.
    const [activeTimestamp, setActiveTimestamp] = useState<number | null>(null);
    const {isMobile} = useUserDevice();

    // Shared with the mobile details panel, so both agree on which points count as "preliminary".
    const preliminaryFrom = useMemo(() => {
        const dashedFromTimestamp = dashedFromDate ? new Date(dashedFromDate).getTime() : undefined;

        return getPreliminaryFrom(rpSeries, rcnSeries, dashedFromTimestamp);
    }, [rpSeries, rcnSeries, dashedFromDate]);

    const activePoint = useMemo<ActiveTrendPoint | null>(() => {
        if (activeTimestamp === null) {
            return null;
        }

        const rpPoint = rpSeries?.find((point) => new Date(point.date).getTime() === activeTimestamp);
        const rcnPoint = rcnSeries.find((point) => new Date(point.date).getTime() === activeTimestamp);

        return {
            timestamp: activeTimestamp,
            isPreliminary: preliminaryFrom !== null && activeTimestamp > preliminaryFrom,
            rpValue: rpPoint?.value ?? null,
            rcnValue: rcnPoint?.value ?? null,
            rcnCount: rcnPoint?.count ?? null
        };
    }, [activeTimestamp, rpSeries, rcnSeries, preliminaryFrom]);

    const options = useMemo<Options>(() => {
        const zones = preliminaryFrom !== null ? [{value: preliminaryFrom, dashStyle: "Solid" as const}, {dashStyle: "ShortDash" as const}] : undefined;

        return {
            chart: {
                // Chart.tsx merges this wholesale - height must be repeated here or the <Chart> height prop is lost.
                height: CHART_HEIGHT,
                spacingBottom: TRANSACTIONS_ROW_HEIGHT,
                events: {
                    render: function (this: ChartWithTrendExtras) {
                        if (isMobile) {
                            // Highcharts' Pointer.reset() clears hover markers on touch-end - no-op it so the
                            // point stays "sticky" like the outline/panel above.
                            this.pointer.reset = () => undefined;
                        }

                        drawTransactionCounts(this, rcnSeries, preliminaryFrom, isMobile ?? false);

                        const nextRect = getPreliminaryBandRect(this, preliminaryFrom);

                        // render() fires every redraw (animation frames included) - skip the update when nothing moved.
                        setPreliminaryBandRect((prevRect) =>
                            prevRect && nextRect && prevRect.left === nextRect.left && prevRect.width === nextRect.width && prevRect.top === nextRect.top
                                ? prevRect
                                : nextRect
                        );
                    }
                }
            },
            title: {text: ""},
            legend: {enabled: false},
            xAxis: {
                ...getDefaultXAxisOptions(),
                type: "datetime" as const,
                plotBands: preliminaryFrom !== null ? [{from: preliminaryFrom, to: Number.MAX_VALUE, color: "var(--colors-gray-100)"}] : undefined,
                plotLines:
                    preliminaryFrom !== null ? [{value: preliminaryFrom, color: "var(--colors-gray-400)", dashStyle: "Dash" as const, width: 1}] : undefined,
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => formatPL(new Date(value), "LLL yyyy")
                }
            },
            yAxis: {
                ...getDefaultYAxisOptions(),
                allowDecimals: getPriceAxisAllowDecimals([...(rpSeries ?? []), ...rcnSeries].map((point) => point.value)),
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => formatPriceAxisLabel(Number(value))
                }
            },
            plotOptions: {
                spline: {
                    marker: {enabled: false},
                    point: {
                        events: {
                            // No mouseOut on mobile - the slightest scroll would clear the panel; stays sticky until tapped again.
                            mouseOver: function (this: Point) {
                                if (isMobile) {
                                    setActiveTransactionBar(this.series.chart as ChartWithTrendExtras, Number(this.x));
                                    setActiveTimestamp(Number(this.x));
                                }
                            }
                        }
                    }
                }
            },
            series: [
                {
                    name: RP_SERIES_NAME,
                    type: "spline" as const,
                    color: TREND_RP_COLOR,
                    lineWidth: 2.5,
                    zoneAxis: "x" as const,
                    zones,
                    data: rpSeries ? toHighchartsData(rpSeries) : []
                },
                {
                    name: RCN_SERIES_NAME,
                    type: "spline" as const,
                    color: TREND_RCN_COLOR,
                    lineWidth: 2.5,
                    zoneAxis: "x" as const,
                    zones,
                    data: toRcnHighchartsData(rcnSeries)
                }
            ],
            tooltip: {
                // Stays enabled on mobile too - disabling it also kills Highcharts' shared hover-point highlighting.
                // The formatter returns "" there instead; PricesTrendMobileDetailsPanel shows the info, since a
                // floating tooltip is awkward to hit/read with a finger.
                shared: true,
                useHTML: true,
                borderWidth: 0,
                backgroundColor: "transparent",
                shadow: false,
                padding: 0,
                // Higher than the "Dane wstępne" pill (z-index 1) - otherwise the tooltip painted behind it.
                style: {color: "var(--colors-secondary)", fontSize: "1.2rem", zIndex: 3},
                formatter: function (this: TooltipFormatterContextObject) {
                    if (isMobile) {
                        return "";
                    }

                    const points = this.points ?? [];
                    const rpPoint = points.find((point) => point.series.name === RP_SERIES_NAME);
                    const rcnPoint = points.find((point) => point.series.name === RCN_SERIES_NAME);
                    const timestamp = Number(this.x);
                    // > not >= - see the same comment in drawTransactionCounts above.
                    const isPreliminary = preliminaryFrom !== null && timestamp > preliminaryFrom;

                    const monthLabel = capitalize(formatPL(new Date(timestamp), "LLLL yyyy"));
                    const pillsHtml = [getTooltipPillHtml(PRICE_STAT_LABEL[priceStat]), getTooltipPillHtml(isPreliminary ? "Dane wstępne" : "Pełne dane")].join(
                        ""
                    );

                    const rcnCustom = rcnPoint ? (rcnPoint.point.options.custom as RcnPointCustom | undefined) : undefined;
                    const rcnCount = rcnCustom?.count;
                    const rcnCountPhrase = rcnCount !== undefined ? `<b>${formatTransactionsCountGenitive(rcnCount)}</b>` : undefined;

                    const rowsHtml = [
                        getTooltipRowHtml({
                            color: TREND_RP_COLOR,
                            label: "Cena Ofertowa",
                            value: typeof rpPoint?.y === "number" ? rpPoint.y : null,
                            description: "Na bazie ofert na portalu rynekpierwotny.pl"
                        }),
                        getTooltipRowHtml({
                            color: TREND_RCN_COLOR,
                            label: "Transakcyjna (RCN)",
                            value: typeof rcnPoint?.y === "number" ? rcnPoint.y : null,
                            description: rcnCountPhrase ? (isPreliminary ? `na bazie tylko ${rcnCountPhrase}` : `na bazie ${rcnCountPhrase}`) : undefined
                        })
                    ];

                    const footerHtml = isPreliminary
                        ? `<hr style="border:none;border-top:1px solid var(--colors-gray-200);margin:1.2rem 0;" /><p style="margin:0;font-size:1.4rem;font-weight:400;line-height:2rem;color:var(--colors-gray-600);">RCN dopisuje akty notarialne z opóźnieniem,<br/>więc ta wartość może się jeszcze zmienić.</p>`
                        : "";

                    return `
                        <div style="min-width:250px;background:var(--colors-white);border-radius:1.6rem;box-shadow:0px 16px 60px 0px #AEB1B499;padding:1.6rem;">
                            <div style="display:flex;align-items:center;justify-content:space-between;gap:0.8rem;margin-bottom:1.2rem;">
                                <span style="font-size:1.4rem;font-weight:500;line-height:2rem;color:var(--colors-secondary);">${monthLabel}</span>
                                <div style="display:flex;gap:0.6rem;">${pillsHtml}</div>
                            </div>
                            <div style="display:flex;flex-direction:column;gap:1.2rem;">${rowsHtml.join("")}</div>
                            ${footerHtml}
                        </div>
                    `;
                }
            }
        };
    }, [rpSeries, rcnSeries, preliminaryFrom, priceStat, isMobile]);

    return (
        <>
            <div className={chartHolderStyle}>
                <Chart
                    height={CHART_HEIGHT}
                    options={options}
                    loader={
                        rpSeries || isLoading ? null : (
                            <CenterBox height={CHART_HEIGHT}>
                                <Loader size="lg" />
                            </CenterBox>
                        )
                    }
                />

                {preliminaryBandRect && (
                    <div
                        className={pillHolderStyle}
                        style={{
                            left: preliminaryBandRect.left + preliminaryBandRect.width / 2,
                            top: preliminaryBandRect.top + PILL_TOP_OFFSET
                        }}
                    >
                        <PricesTrendPreliminaryPill />
                    </div>
                )}
            </div>

            {isMobile && <PricesTrendMobileDetailsPanel activePoint={activePoint} priceStat={priceStat} />}
        </>
    );
};

const chartHolderStyle = css({
    position: "relative",
    width: "100%"
});

const pillHolderStyle = css({
    position: "absolute",
    transform: "translateX(-50%)",
    zIndex: 1
});
import {useMemo, useState} from "react";
import {css} from "@panda/css";
import type {Options, Point, TooltipFormatterContextObject} from "highcharts";

import {CenterBox} from "@pg-design/grid-module";
import {Loader} from "@pg-design/loader-module";
import {Chart, getDefaultXAxisOptions, getDefaultYAxisOptions} from "@pg-mono/charts";
import {formatPL} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {PRICE_STAT_LABEL, PriceStat} from "../../constants/price_stat";
import {TREND_RCN_COLOR, TREND_RP_COLOR} from "../../constants/trend_series_colors";
import {TrendSeriesPoint} from "../../types/TrendSeriesPoint";
import {formatTransactionsCountGenitive} from "../../utils/format_transactions_count";
import {formatPriceAxisLabel, getPriceAxisAllowDecimals} from "../../utils/price_axis";
import {CHART_HEIGHT, PILL_TOP_OFFSET, RCN_SERIES_NAME, RP_SERIES_NAME, TRANSACTIONS_ROW_HEIGHT} from "./constants/chart_constants";
import {
    ChartWithTrendExtras,
    drawTransactionCounts,
    getPreliminaryBandRect,
    getPreliminaryFrom,
    getTooltipPillHtml,
    getTooltipRowHtml,
    PreliminaryBandRect,
    RcnPointCustom,
    setActiveTransactionBar,
    toHighchartsData,
    toRcnHighchartsData
} from "./utils/chart_utils";
import {capitalize} from "./prices_trend_format";
import {ActiveTrendPoint, PricesTrendMobileDetailsPanel} from "./PricesTrendMobileDetailsPanel";
import {PricesTrendPreliminaryPill} from "./PricesTrendPreliminaryPill";

interface Props {
    rpSeries: TrendSeriesPoint[] | null;
    rcnSeries: TrendSeriesPoint[];
    // Points at/after this date render dashed - a visual cue they're still filling in, not settled.
    dashedFromDate?: string;
    priceStat: PriceStat;
    // True while RCN refetches on a filter change - a separate overlay, since rpSeries already has data then.
    isLoading?: boolean;
}

export const PricesTrendChart = (props: Props) => {
    const {rpSeries, rcnSeries, dashedFromDate, priceStat, isLoading} = props;
    const [preliminaryBandRect, setPreliminaryBandRect] = useState<PreliminaryBandRect | null>(null);
    // The point tapped on mobile - drives PricesTrendMobileDetailsPanel as React state, and (via
    // setActiveTransactionBar) the bar outline drawn straight onto the chart's own SVG.
    const [activeTimestamp, setActiveTimestamp] = useState<number | null>(null);
    const {isMobile} = useUserDevice();

    // Shared with the mobile details panel, so both agree on which points count as "preliminary".
    const preliminaryFrom = useMemo(() => {
        const dashedFromTimestamp = dashedFromDate ? new Date(dashedFromDate).getTime() : undefined;

        return getPreliminaryFrom(rpSeries, rcnSeries, dashedFromTimestamp);
    }, [rpSeries, rcnSeries, dashedFromDate]);

    const activePoint = useMemo<ActiveTrendPoint | null>(() => {
        if (activeTimestamp === null) {
            return null;
        }

        const rpPoint = rpSeries?.find((point) => new Date(point.date).getTime() === activeTimestamp);
        const rcnPoint = rcnSeries.find((point) => new Date(point.date).getTime() === activeTimestamp);

        return {
            timestamp: activeTimestamp,
            isPreliminary: preliminaryFrom !== null && activeTimestamp > preliminaryFrom,
            rpValue: rpPoint?.value ?? null,
            rcnValue: rcnPoint?.value ?? null,
            rcnCount: rcnPoint?.count ?? null
        };
    }, [activeTimestamp, rpSeries, rcnSeries, preliminaryFrom]);

    const options = useMemo<Options>(() => {
        const zones = preliminaryFrom !== null ? [{value: preliminaryFrom, dashStyle: "Solid" as const}, {dashStyle: "ShortDash" as const}] : undefined;

        return {
            chart: {
                // Chart.tsx merges this wholesale - height must be repeated here or the <Chart> height prop is lost.
                height: CHART_HEIGHT,
                spacingBottom: TRANSACTIONS_ROW_HEIGHT,
                events: {
                    render: function (this: ChartWithTrendExtras) {
                        if (isMobile) {
                            // Highcharts' Pointer.reset() clears hover markers on touch-end - no-op it so the
                            // point stays "sticky" like the outline/panel above.
                            this.pointer.reset = () => undefined;
                        }

                        drawTransactionCounts(this, rcnSeries, preliminaryFrom, isMobile ?? false);

                        const nextRect = getPreliminaryBandRect(this, preliminaryFrom);

                        // render() fires every redraw (animation frames included) - skip the update when nothing moved.
                        setPreliminaryBandRect((prevRect) =>
                            prevRect && nextRect && prevRect.left === nextRect.left && prevRect.width === nextRect.width && prevRect.top === nextRect.top
                                ? prevRect
                                : nextRect
                        );
                    }
                }
            },
            title: {text: ""},
            legend: {enabled: false},
            xAxis: {
                ...getDefaultXAxisOptions(),
                type: "datetime" as const,
                plotBands: preliminaryFrom !== null ? [{from: preliminaryFrom, to: Number.MAX_VALUE, color: "var(--colors-gray-100)"}] : undefined,
                plotLines:
                    preliminaryFrom !== null ? [{value: preliminaryFrom, color: "var(--colors-gray-400)", dashStyle: "Dash" as const, width: 1}] : undefined,
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => formatPL(new Date(value), "LLL yyyy")
                }
            },
            yAxis: {
                ...getDefaultYAxisOptions(),
                allowDecimals: getPriceAxisAllowDecimals([...(rpSeries ?? []), ...rcnSeries].map((point) => point.value)),
                labels: {
                    enabled: true,
                    style: {color: "var(--colors-gray-700)", fontSize: "1.106rem"},
                    formatter: ({value}: {value: string | number}) => formatPriceAxisLabel(Number(value))
                }
            },
            plotOptions: {
                spline: {
                    marker: {enabled: false},
                    point: {
                        events: {
                            // No mouseOut on mobile - the slightest scroll would clear the panel; stays sticky until tapped again.
                            mouseOver: function (this: Point) {
                                if (isMobile) {
                                    setActiveTransactionBar(this.series.chart as ChartWithTrendExtras, Number(this.x));
                                    setActiveTimestamp(Number(this.x));
                                }
                            }
                        }
                    }
                }
            },
            series: [
                {
                    name: RP_SERIES_NAME,
                    type: "spline" as const,
                    color: TREND_RP_COLOR,
                    lineWidth: 2.5,
                    zoneAxis: "x" as const,
                    zones,
                    data: rpSeries ? toHighchartsData(rpSeries) : []
                },
                {
                    name: RCN_SERIES_NAME,
                    type: "spline" as const,
                    color: TREND_RCN_COLOR,
                    lineWidth: 2.5,
                    zoneAxis: "x" as const,
                    zones,
                    data: toRcnHighchartsData(rcnSeries)
                }
            ],
            tooltip: {
                // Stays enabled on mobile too - disabling it also kills Highcharts' shared hover-point highlighting.
                // The formatter returns "" there instead; PricesTrendMobileDetailsPanel shows the info, since a
                // floating tooltip is awkward to hit/read with a finger.
                shared: true,
                useHTML: true,
                borderWidth: 0,
                backgroundColor: "transparent",
                shadow: false,
                padding: 0,
                // Higher than the "Dane wstępne" pill (z-index 1) - otherwise the tooltip painted behind it.
                style: {color: "var(--colors-secondary)", fontSize: "1.2rem", zIndex: 3},
                formatter: function (this: TooltipFormatterContextObject) {
                    if (isMobile) {
                        return "";
                    }

                    const points = this.points ?? [];
                    const rpPoint = points.find((point) => point.series.name === RP_SERIES_NAME);
                    const rcnPoint = points.find((point) => point.series.name === RCN_SERIES_NAME);
                    const timestamp = Number(this.x);
                    // > not >= - see the same comment in drawTransactionCounts above.
                    const isPreliminary = preliminaryFrom !== null && timestamp > preliminaryFrom;

                    const monthLabel = capitalize(formatPL(new Date(timestamp), "LLLL yyyy"));
                    const pillsHtml = [getTooltipPillHtml(PRICE_STAT_LABEL[priceStat]), getTooltipPillHtml(isPreliminary ? "Dane wstępne" : "Pełne dane")].join(
                        ""
                    );

                    const rcnCustom = rcnPoint ? (rcnPoint.point.options.custom as RcnPointCustom | undefined) : undefined;
                    const rcnCount = rcnCustom?.count;
                    const rcnCountPhrase = rcnCount !== undefined ? `<b>${formatTransactionsCountGenitive(rcnCount)}</b>` : undefined;

                    const rowsHtml = [
                        getTooltipRowHtml({
                            color: TREND_RP_COLOR,
                            label: "Cena Ofertowa",
                            value: typeof rpPoint?.y === "number" ? rpPoint.y : null,
                            description: "Na bazie ofert na portalu rynekpierwotny.pl"
                        }),
                        getTooltipRowHtml({
                            color: TREND_RCN_COLOR,
                            label: "Transakcyjna (RCN)",
                            value: typeof rcnPoint?.y === "number" ? rcnPoint.y : null,
                            description: rcnCountPhrase ? (isPreliminary ? `na bazie tylko ${rcnCountPhrase}` : `na bazie ${rcnCountPhrase}`) : undefined
                        })
                    ];

                    const footerHtml = isPreliminary
                        ? `<hr style="border:none;border-top:1px solid var(--colors-gray-200);margin:1.2rem 0;" /><p style="margin:0;font-size:1.4rem;font-weight:400;line-height:2rem;color:var(--colors-gray-600);">RCN dopisuje akty notarialne z opóźnieniem,<br/>więc ta wartość może się jeszcze zmienić.</p>`
                        : "";

                    return `
                        <div style="min-width:250px;background:var(--colors-white);border-radius:1.6rem;box-shadow:0px 16px 60px 0px #AEB1B499;padding:1.6rem;">
                            <div style="display:flex;align-items:center;justify-content:space-between;gap:0.8rem;margin-bottom:1.2rem;">
                                <span style="font-size:1.4rem;font-weight:500;line-height:2rem;color:var(--colors-secondary);">${monthLabel}</span>
                                <div style="display:flex;gap:0.6rem;">${pillsHtml}</div>
                            </div>
                            <div style="display:flex;flex-direction:column;gap:1.2rem;">${rowsHtml.join("")}</div>
                            ${footerHtml}
                        </div>
                    `;
                }
            }
        };
    }, [rpSeries, rcnSeries, preliminaryFrom, priceStat, isMobile]);

    return (
        <>
            <div className={chartHolderStyle}>
                <Chart
                    height={CHART_HEIGHT}
                    options={options}
                    loader={
                        rpSeries || isLoading ? null : (
                            <CenterBox height={CHART_HEIGHT}>
                                <Loader size="lg" />
                            </CenterBox>
                        )
                    }
                />

                {preliminaryBandRect && (
                    <div
                        className={pillHolderStyle}
                        style={{
                            left: preliminaryBandRect.left + preliminaryBandRect.width / 2,
                            top: preliminaryBandRect.top + PILL_TOP_OFFSET
                        }}
                    >
                        <PricesTrendPreliminaryPill />
                    </div>
                )}
            </div>

            {isMobile && <PricesTrendMobileDetailsPanel activePoint={activePoint} priceStat={priceStat} />}
        </>
    );
};

const chartHolderStyle = css({
    position: "relative",
    width: "100%"
});

const pillHolderStyle = css({
    position: "absolute",
    transform: "translateX(-50%)",
    zIndex: 1
});
